<script>
  import { onMount } from "svelte";
  import { Label, Input } from "sveltestrap";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import {
    callQueue,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    directContext,
    currMenu,
  } from "../../editor/storage.js";
  import {
    projectionTypes,
    customMaps,
  } from "../../editor/component/projection.js";
  import ItemResponse from "../component/ItemResponse.svelte";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;

  let projectionType,
    projectionLevel,
    projectionScale,
    projectionTranslateX,
    projectionTranslateY,
    projectionCenterX,
    projectionCenterY,
    projectionRepeat,
    projectionCustom;

  function setValues() {
    projectionType = $currentSpec.fronto?.layout?.projection?.type;
    projectionLevel = $currentSpec.fronto?.layout?.projection?.level;
    projectionScale = $currentSpec.fronto?.layout?.projection?.scale;
    projectionTranslateX =
      $currentSpec.fronto?.layout?.projection?.translate?.[0] || 0;
    projectionTranslateY =
      $currentSpec.fronto?.layout?.projection?.translate?.[1] || 0;
    projectionRepeat = $currentSpec.fronto?.layout?.projection?.repeat;
    projectionCenterX =
      $currentSpec.fronto?.layout?.projection?.center?.[0] || 0;
    projectionCenterY =
      $currentSpec.fronto?.layout?.projection?.center?.[1] || 0;
    projectionCustom = $currentSpec.fronto?.layout?.projection?.customMap;
  }

  function updateProjection(event, role) {
    let rule;
    if (["type", "level", "scale"].includes(role)) {
      let newValue = event.target.value;
      rule = {
        description: "Update proejction " + role,
        specifier: { role: "view" },
        action: "modify",
        option: { projection: {} },
      };
      console.log(parseFloat(newValue));
      rule.option.projection[role] =
        role === "scale" ? parseFloat(newValue) : newValue;
    } else if (["translate_x", "translate_y"].includes(role)) {
      let newValue = parseFloat(event.target.value);
      rule = {
        description: "Update proejction " + role.split("_").join(" "),
        specifier: { role: "view" },
        action: "modify",
        option: { projection: {} },
      };
      rule.option.projection["translate"] =
        role === "translate_x"
          ? [newValue, projectionTranslateY || 0]
          : [projectionTranslateX || 0, newValue];
    } else if (["center_x", "center_y"].includes(role)) {
      let newValue = parseFloat(event.target.value);
      rule = {
        description: "Update proejction " + role.split("_").join(" "),
        specifier: { role: "view" },
        action: "modify",
        option: { projection: {} },
      };
      rule.option.projection["center"] =
        role === "center_x"
          ? [newValue, projectionCenterY || 0]
          : [projectionCenterX || 0, newValue];
      console.log(newValue);
    } else if (role === "repeat") {
      let newValue = event.target.checked;
      rule = {
        description: "Update proejction repetition",
        specifier: { role: "view" },
        action: "modify",
        option: { projection: { repeat: newValue } },
      };
    } else if (role === "custom") {
      let newValue = event.target.value;
      rule = {
        description: "Update custom proejction",
        specifier: { role: "view" },
        action: "modify",
        option: {
          projection: {
            customMap: newValue,
          },
        },
      };
    }
    if (rule) {
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  // pipelines
  onMount(() => {
    setValues();
  });
  currentSpec.subscribe(() => {
    setValues();
  });
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["projection"]?.title || "Projection"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if $currentSpec?.fronto?.layout?.composition !== "projection"}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">
          Set the layout type as <em>projection</em>! Current: {$currentSpec
            ?.fronto?.layout?.composition}
        </p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">
          Set projection types, scale, and translate. Currently maps for the
          world and USA are available. Custom map support is under
          implementation.
        </p>
      </div>
    </div>
    <!-- map type, level, scale(zoom), translate(pan) -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="projection_type">Projection type</Label>
      </div>
      <div class="dupo-menu-pane-form-row">
        <Input
          name="projection_type"
          type="select"
          class="custom-form-select"
          on:focus={inputFocusAction}
          on:blur={inputBlurAction}
          on:change={(event) => {
            updateProjection(event, "type");
          }}
          bind:value={projectionType}
        >
          <option value={undefined}>Select</option>
          {#each projectionTypes as p}
            <option value={p}>{p}</option>
          {/each}
        </Input>
      </div>
    </div>
    <!-- custom map -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="projection_type">Custom Map</Label>
      </div>
      <div class="dupo-menu-pane-form-row">
        <Input
          name="projection_type"
          type="select"
          class="custom-form-select"
          on:focus={inputFocusAction}
          on:blur={inputBlurAction}
          on:change={(event) => {
            updateProjection(event, "custom");
          }}
          bind:value={projectionCustom}
        >
          <option value={undefined}>Select</option>
          {#each customMaps as p}
            <option value={p}>{p}</option>
          {/each}
        </Input>
      </div>
    </div>
    <!-- level  (for US)-->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="projection_level">Level</Label
        >{#if projectionType === "albersUsa"}<small> for US maps</small>{/if}
      </div>
      <div class="dupo-menu-pane-form-row">
        <Input
          name="projection_level"
          type="select"
          class="custom-form-select"
          on:focus={inputFocusAction}
          on:blur={inputBlurAction}
          on:change={(event) => {
            updateProjection(event, "level");
          }}
          bind:value={projectionLevel}
        >
          <option value={undefined}>Select</option>
          <option value="country">country</option>
          {#if projectionType === "albersUsa"}
            <option value="us-state">us-state</option>
            <option value="us-county">us-county</option>
          {/if}
        </Input>
      </div>
    </div>

    <!-- scale -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="projection_scale">Scale</Label>
      </div>
      <div class="dupo-menu-pane-form-row">
        <Input
          name="projection_scale"
          type="number"
          class="custom-form-select"
          autocomplete="off"
          on:focus={inputFocusAction}
          on:blur={inputBlurAction}
          on:change={(event) => {
            updateProjection(event, "scale");
          }}
          bind:value={projectionScale}
        />
      </div>
    </div>

    <!-- center -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label>Center</Label>
      </div>
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Drag the slider to move the map center</p>
      </div>
      <ItemResponse
        type="slider"
        title="Longitude (horizontal center)"
        token="center_x"
        value={projectionCenterX}
        min={-180}
        max={180}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateProjection(event, "center_x");
        }}
      />
      <ItemResponse
        type="slider"
        title="Latitude (vertical center)"
        token="center_y"
        value={projectionCenterY}
        min={-90}
        max={90}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateProjection(event, "center_y");
        }}
      />
    </div>

    <!-- repetition -->
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        type="inline-check"
        title="Repeat map (horizontally only)"
        token="repeat"
        value={projectionRepeat}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateProjection(event, "repeat");
        }}
      />
    </div>

    <!-- translate -->
    <!-- <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label>Translate</Label>
      </div>
      <div class="dupo-menu-pane-row">
        <div class="d-flex">
          <Label for="projection_translate_x" class="mb-0" style="width: 50%;"
            >X<i class="bi bi-arrow-bar-right ms-2" /></Label
          >
          <Label for="projection_translate_y" class="mb-0" style="width: 50%;"
            >Y<i class="bi bi-arrow-bar-up ms-2" /></Label
          >
        </div>
      </div>
      <div class="dupo-menu-pane-form-row">
        <div class="form-wrap">
          <Input
            name="projection_translate_x"
            type="number"
            class="custom-form-select"
            style="border-right: 1px solid white;"
            autocomplete="off"
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateProjection(event, "translate_x");
            }}
            bind:value={projectionTranslateX}
          />
          <Input
            name="projection_translate_y"
            type="number"
            class="custom-form-select"
            style="border-left: 1px solid white;"
            autocomplete="off"
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateProjection(event, "translate_y");
            }}
            bind:value={projectionTranslateY}
          />
        </div>
      </div>
    </div> -->
    {#if $directContext}
      <div class="pane-divider" />
      <div class="dupo-menu-pane mb-5">
        <div class="dupo-menu-pane-button-row">
          <button
            type="button"
            on:click={() => {
              currMenu.set("selection");
            }}><i class="bi bi-arrow-left me-1" /> Go back</button
          >
        </div>
      </div>
    {/if}
  {/if}
</section>
